import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, DollarSign, Clock, Tag, Eye, MapPin, Calendar, Paperclip, ArrowLeft, MessageSquare, Send, Check, X as XIcon, FileText, Image as ImageIcon, Video as VideoIcon, Trophy } from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_PILL = {
  open:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  awarded: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
  closed:  'bg-zinc-500/15 text-zinc-300 border-zinc-500/20',
};
const OFFER_PILL = {
  pending:   'bg-cyan-500/15 text-cyan-300 border-cyan-500/20',
  accepted:  'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
  rejected:  'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
  withdrawn: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};
const fileIcon = (ct) => (ct?.startsWith('image/') ? ImageIcon : ct?.startsWith('video/') ? VideoIcon : FileText);

/** /jobs/:id — Single RFQ. Owner sees the offers list + Award buttons.
 *  Vendor sees their own offer status + an Offer composer if not yet submitted.
 *  Anonymous viewers see a sign-in prompt. */
export default function JobRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/rfq/${id}`, { headers: user ? headers() : {} });
      if (res.ok) setData(await res.json());
      else if (res.status === 404) navigate('/jobs');
    } finally { setLoading(false); }
  }, [id, user, headers, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen bg-[#0B0D2B] text-white flex items-center justify-center"><Loader2 className="animate-spin text-fuchsia-400" /></div>;
  }
  if (!data) return null;

  const isOwner = data.viewer_is_owner;
  const isVendor = !!(user?.roles?.includes('vendor') || user?.role === 'vendor');
  const canBid = !isOwner && isVendor && data.status === 'open' && !data.viewer_has_offer;

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 pt-24 pb-12" data-testid="rfq-detail">
        <button onClick={() => navigate('/jobs')} className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm mb-5">
          <ArrowLeft size={14} /> Back to all requests
        </button>

        {/* Header card */}
        <article className="rounded-3xl bg-[#13153A]/80 border border-white/10 p-5 sm:p-6 mb-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h1 className="text-xl sm:text-2xl font-bold font-syne flex-1">{data.title}</h1>
            <span className={`px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase ${STATUS_PILL[data.status] || STATUS_PILL.open}`} data-testid="rfq-status">
              {data.status}
            </span>
          </div>

          <div className="flex items-center gap-3 text-zinc-400 text-xs mb-4">
            {data.consumer?.profile_image ? (
              <img src={data.consumer.profile_image} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-fuchsia-500/30 flex items-center justify-center text-[11px] font-bold text-white">{(data.consumer?.name || '?')[0]}</div>
            )}
            <span>{data.consumer?.name}</span>
            <span>·</span>
            <span>{fmtAgo(data.created_at)}</span>
          </div>

          <p className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed mb-5" data-testid="rfq-description-display">{data.description}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {data.category_name && (
              <Stat icon={Tag} label="Category" value={data.category_name} color="text-fuchsia-300" />
            )}
            {(data.budget_min || data.budget_max) && (
              <Stat icon={DollarSign} label="Budget" value={`${data.budget_min || '?'}–${data.budget_max || '?'} ${data.currency || 'USD'}`} color="text-yellow-400" />
            )}
            {data.turnaround_days && (
              <Stat icon={Clock} label="Turnaround" value={`${data.turnaround_days} days`} color="text-cyan-300" />
            )}
            {data.deadline_iso && (
              <Stat icon={Calendar} label="Need by" value={data.deadline_iso.slice(0, 10)} color="text-rose-300" />
            )}
            {data.location && (
              <Stat icon={MapPin} label="Location" value={data.location} color="text-emerald-300" />
            )}
            <Stat icon={Eye} label="Views" value={data.view_count || 0} color="text-zinc-300" />
          </div>

          {data.attachments && data.attachments.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-zinc-300 flex items-center gap-1 mb-2">
                <Paperclip size={11} /> Examples & references
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {data.attachments.map((a, i) => {
                  const Icon = fileIcon(a.content_type);
                  const isImage = a.content_type?.startsWith('image/');
                  return (
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" className="rounded-lg bg-white/5 border border-white/10 hover:border-fuchsia-500/30 overflow-hidden flex flex-col" data-testid={`rfq-attach-${i}`}>
                      <div className="aspect-square bg-black flex items-center justify-center">
                        {isImage ? <img src={a.url} alt="" className="w-full h-full object-cover" /> : <Icon size={24} className="text-fuchsia-300" />}
                      </div>
                      <span className="px-2 py-1.5 text-[11px] text-zinc-300 truncate">{a.filename || `File ${i + 1}`}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </article>

        {/* Sign-in prompt */}
        {!user && (
          <div className="rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/30 p-5 mb-6">
            <p className="text-white font-semibold">Sign in to submit an offer.</p>
            <p className="text-zinc-300 text-sm mt-1">Vendors can quote and start a conversation directly.</p>
            <Link to={`/auth?next=/jobs/${id}`} className="inline-block mt-3 px-4 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold">Sign in</Link>
          </div>
        )}

        {/* Vendor's own offer / submit form */}
        {user && !isOwner && (
          <>
            {data.viewer_has_offer ? (
              <YourOfferCard offer={data.viewer_offer} onWithdraw={load} />
            ) : canBid ? (
              <SubmitOfferForm requestId={id} onSubmitted={load} />
            ) : !isVendor ? (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mb-6 text-center">
                <p className="text-zinc-300 text-sm">Only vendors can submit offers.</p>
                <Link to="/onboarding/vendor" className="inline-block mt-3 px-4 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold">Become a vendor →</Link>
              </div>
            ) : data.status !== 'open' ? (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mb-6 text-center text-zinc-400 text-sm">
                This request is no longer open for offers.
              </div>
            ) : null}
          </>
        )}

        {/* Owner: offers list */}
        {isOwner && (
          <section data-testid="rfq-offers-list">
            <h2 className="text-lg font-bold font-syne mb-3">{data.offers?.length || 0} {data.offers?.length === 1 ? 'offer' : 'offers'}</h2>
            {data.offers?.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-zinc-400 text-sm">
                Vendors are reviewing your request. Offers will show up here.
              </div>
            ) : (
              <div className="space-y-3">
                {data.offers.map((o) => (
                  <OfferCard
                    key={o.offer_id}
                    offer={o}
                    requestId={id}
                    canAward={data.status === 'open'}
                    onAwarded={load}
                  />
                ))}
              </div>
            )}
            {data.status === 'open' && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={async () => {
                    if (!confirm('Close this request? You won\'t accept new offers.')) return;
                    const res = await fetch(`${API}/rfq/${id}/close`, { method: 'POST', headers: headers() });
                    if (res.ok) { toast.success('Closed'); load(); }
                  }}
                  className="text-xs text-zinc-400 hover:text-red-300"
                  data-testid="rfq-close"
                >
                  Close this request
                </button>
              </div>
            )}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color = 'text-zinc-300' }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
    <div className={`flex items-center gap-1.5 ${color} text-[10px] uppercase tracking-wider font-semibold mb-0.5`}>
      <Icon size={10} /> {label}
    </div>
    <div className="text-white text-sm font-semibold truncate">{value}</div>
  </div>
);

function SubmitOfferForm({ requestId, onSubmitted }) {
  const [form, setForm] = useState({ price: '', turnaround_days: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) return toast.error('Add a bit more pitch (min 10 chars)');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/rfq/${requestId}/offers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          price: form.price ? Number(form.price) : null,
          turnaround_days: form.turnaround_days ? Number(form.turnaround_days) : null,
          message: form.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Could not submit'); setSubmitting(false); return; }
      toast.success('Offer submitted!');
      onSubmitted?.();
    } catch { toast.error('Network error'); setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-fuchsia-500/5 border border-fuchsia-500/20 p-5 mb-6 space-y-3" data-testid="submit-offer-form">
      <h2 className="text-white font-syne text-lg font-bold">Submit your offer</h2>
      <p className="text-zinc-400 text-xs">Be specific. Show the buyer why you're the right fit. Early offers win 3× more often.</p>
      <div className="grid grid-cols-2 gap-3">
        <input type="number" min="0" placeholder="Your price (USD)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="form-input" data-testid="offer-price" />
        <input type="number" min="1" placeholder="Turnaround (days)" value={form.turnaround_days} onChange={(e) => setForm({ ...form, turnaround_days: e.target.value })} className="form-input" data-testid="offer-turnaround" />
      </div>
      <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} maxLength={2000} placeholder="Hi! I can deliver this in 24h with PSD source files included…" className="form-input resize-none" data-testid="offer-message" />
      <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="offer-submit">
        {submitting && <Loader2 size={14} className="animate-spin" />}
        <Send size={14} /> Send offer
      </button>
    </form>
  );
}

function YourOfferCard({ offer, onWithdraw }) {
  const withdraw = async () => {
    if (!confirm('Withdraw your offer?')) return;
    const res = await fetch(`${API}/rfq/offers/${offer.offer_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ withdraw: true }),
    });
    if (res.ok) { toast.success('Withdrawn'); onWithdraw?.(); }
  };
  return (
    <div className="rounded-2xl bg-cyan-500/5 border border-cyan-500/20 p-4 mb-6 flex items-center justify-between gap-3" data-testid="your-offer">
      <div>
        <p className="text-white font-semibold text-sm">Your offer is in.</p>
        <p className="text-zinc-400 text-xs mt-0.5">Status: <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${OFFER_PILL[offer.status] || OFFER_PILL.pending}`}>{offer.status}</span></p>
      </div>
      {offer.status === 'pending' && (
        <button onClick={withdraw} className="text-xs text-red-300 hover:text-red-200 font-semibold" data-testid="your-offer-withdraw">Withdraw</button>
      )}
    </div>
  );
}

function OfferCard({ offer, requestId, canAward, onAwarded }) {
  const navigate = useNavigate();
  const [working, setWorking] = useState(false);

  const award = async () => {
    if (!confirm(`Award the job to ${offer.vendor?.business_name || offer.vendor?.name}?`)) return;
    setWorking(true);
    try {
      const res = await fetch(`${API}/rfq/${requestId}/award/${offer.offer_id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast.success('Awarded! Conversation started.'); onAwarded?.(); }
      else toast.error(data.detail || 'Could not award');
    } finally { setWorking(false); }
  };

  return (
    <article className={`rounded-2xl border p-4 ${offer.status === 'accepted' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-white/5 border-white/10'}`} data-testid={`offer-card-${offer.offer_id}`}>
      <div className="flex items-start gap-3">
        {offer.vendor?.profile_image ? (
          <img src={offer.vendor.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-fuchsia-500/30 flex items-center justify-center font-bold text-white">{(offer.vendor?.name || '?')[0]}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {offer.vendor?.vendor_id ? (
              <Link to={`/vendor/${offer.vendor.vendor_id}`} className="text-white font-semibold text-sm hover:text-fuchsia-300">
                {offer.vendor.business_name || offer.vendor.name}
              </Link>
            ) : (
              <span className="text-white font-semibold text-sm">{offer.vendor?.name}</span>
            )}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${OFFER_PILL[offer.status] || OFFER_PILL.pending}`}>
              {offer.status === 'accepted' ? '🏆 awarded' : offer.status}
            </span>
            <span className="text-zinc-500 text-[11px]">· {fmtAgo(offer.created_at)}</span>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-zinc-300 text-xs">
            {offer.price && <span className="flex items-center gap-1"><DollarSign size={11} className="text-yellow-400" />{offer.price} {offer.currency}</span>}
            {offer.turnaround_days && <span className="flex items-center gap-1"><Clock size={11} className="text-cyan-300" />{offer.turnaround_days}d</span>}
          </div>
          <p className="text-zinc-200 text-sm mt-2 whitespace-pre-wrap">{offer.message}</p>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => navigate(`/messages/${offer.vendor_user_id}`)} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-200 hover:text-white text-xs flex items-center gap-1.5" data-testid={`offer-message-${offer.offer_id}`}>
              <MessageSquare size={12} /> Message
            </button>
            {canAward && offer.status === 'pending' && (
              <button onClick={award} disabled={working} className="px-3 py-1.5 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold flex items-center gap-1.5 disabled:opacity-60" data-testid={`offer-award-${offer.offer_id}`}>
                {working ? <Loader2 size={12} className="animate-spin" /> : <Trophy size={12} />}
                Award
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
